import { useCallback, useMemo } from 'react'
import type { EmojiContextValue } from 'stream-chat-react'
import type { ReactionsListProps } from '../ReactionsList'
import { useMessageContext } from 'stream-chat-react'
import type { DefaultStreamChatGenerics } from '../../../types'
import { ReactionEmoji, getStrippedEmojiData } from 'components/Chat/components/Channel/emojiData'

type SharedReactionListProps =
  | 'additionalEmojiProps'
  | 'own_reactions'
  | 'reaction_counts'
  | 'reactionOptions'
  | 'reactions'

type UseProcessReactionsParams = Pick<ReactionsListProps, SharedReactionListProps> &
  Pick<EmojiContextValue, 'emojiConfig'>

// Inverse of the `emoji_<hex>_<hex>` encoding used when sending reactions
const decodeEmojiType = (type: string): string => {
  try {
    return String.fromCodePoint(
      ...type
        .slice('emoji_'.length)
        .split('_')
        .map((hex) => parseInt(hex, 16))
    )
  } catch {
    return ''
  }
}

export const useProcessReactions = <
  StreamChatGenerics extends DefaultStreamChatGenerics = DefaultStreamChatGenerics
>(
  params: UseProcessReactionsParams
) => {
  const {
    additionalEmojiProps = {},
    emojiConfig,
    own_reactions: propOwnReactions,
    reaction_counts: propReactionCounts,
    reactionOptions: propReactionOptions,
    reactions: propReactions,
  } = params
  const { message } = useMessageContext<StreamChatGenerics>('ReactionsList')
  const { defaultMinimalEmojis, emojiData: fullEmojiData, emojiSetDef } = emojiConfig || {}

  const latestReactions = useMemo(
    () => propReactions || message.latest_reactions || [],
    [propReactions, message.latest_reactions]
  )

  const ownReactions = useMemo(
    () => propOwnReactions || message?.own_reactions || [],
    [propOwnReactions, message?.own_reactions]
  )

  const reactionCounts = useMemo(
    () => propReactionCounts || message.reaction_counts || [],
    [propReactionCounts, message.reaction_counts]
  )
  const reactionOptions = propReactionOptions || defaultMinimalEmojis
  const reactionsAreCustom = !!propReactionOptions?.length

  const iHaveReactedWithReaction = useCallback(
    (reactionType: string) => ownReactions.find((reaction) => reaction.type === reactionType),
    [ownReactions]
  )

  const getEmojiByReactionType = useCallback(
    (type: string): ReactionEmoji | undefined =>
      reactionOptions.find((option: ReactionEmoji) => option.id === type),
    [reactionOptions]
  )

  const emojiData = useMemo(
    () => (reactionsAreCustom ? fullEmojiData : getStrippedEmojiData(fullEmojiData)),
    [fullEmojiData, reactionsAreCustom]
  )

  const latestReactionTypes = useMemo(
    () =>
      latestReactions.reduce((reactionTypes, { type }) => {
        if (reactionTypes.indexOf(type) === -1) {
          reactionTypes.push(type)
        }
        return reactionTypes
      }, [] as string[]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [latestReactions]
  )

  // Create mapping from encoded emoji types to actual emoji characters
  const emojiByType = useMemo(() => {
    const map: Record<string, string> = {}
    latestReactions.forEach((reaction: any) => {
      if (!reaction.type.startsWith('emoji_')) return
      if (reaction.emoji) {
        map[reaction.type] = reaction.emoji
      } else if (!map[reaction.type]) {
        // Reactions added by clicking a chip used to be saved without `emoji`;
        // the type itself encodes the code points (emoji_1f44d, emoji_2764_fe0f).
        const decoded = decodeEmojiType(reaction.type)
        if (decoded) map[reaction.type] = decoded
      }
    })
    return map
  }, [latestReactions])

  const supportedReactionMap = useMemo(
    () =>
      reactionOptions.reduce((acc, { id }) => {
        acc[id] = true
        return acc
      }, {} as Record<string, boolean>),
    [reactionOptions]
  )

  // Check if reaction is a native emoji or encoded emoji
  const isNativeEmoji = (type: string) => {
    return (type.length <= 4 && !supportedReactionMap[type]) || type.startsWith('emoji_')
  }

  const supportedReactionsArePresent: any = useMemo(
    () => latestReactionTypes.some((type) => supportedReactionMap[type] || isNativeEmoji(type)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [latestReactionTypes, supportedReactionMap]
  )

  const totalReactionCount = useMemo(
    () =>
      supportedReactionsArePresent
        ? Object.values(reactionCounts).reduce((total, count) => total + count, 0)
        : 0,
    [reactionCounts, supportedReactionsArePresent]
  )

  const aggregatedUserNamesByType = useMemo(
    () =>
      latestReactions.reduce<Record<string, Array<string>>>((typeMap, { type, user }) => {
        typeMap[type] ??= []
        typeMap[type].push(user?.name || user!.id)
        return typeMap
      }, {}),
    // react-hooks/exhaustive-deps
    [latestReactions]
  )

  return {
    additionalEmojiProps: reactionsAreCustom ? additionalEmojiProps : emojiSetDef,
    aggregatedUserNamesByType,
    emojiData,
    emojiByType,
    getEmojiByReactionType,
    iHaveReactedWithReaction,
    latestReactions,
    latestReactionTypes,
    reactionCounts,
    supportedReactionsArePresent,
    totalReactionCount,
  }
}
